import { IconCheck, IconChevronDown } from "@/assets/icons";
import { background, button, divider, state, text } from "@/theme/colors";
import { rounded, spacing } from "@/theme/variables";
import { FCC } from "@/types/common";
import { AccordionSummary, Box, css, Accordion as MuiAccordion, Stack, styled, Typography } from "@mui/material";
import { ReactNode } from "react";

export type RenewVisaStepCardProps = {
  title: string;
  subTitle: string;
  buttons?: ReactNode;
  isCompleted?: boolean;
  disabled?: boolean;
  isSkip?: boolean;
};

export const RenewVisaStepCard: FCC<RenewVisaStepCardProps> = ({
  title,
  subTitle,
  buttons,
  children,
  isCompleted,
  disabled = false,
  isSkip = false,
}) => {
  return (
    <MuiAccordionWrapper
      disabledStep={disabled}
      defaultExpanded
      sx={{ boxShadow: "none", pointerEvents: disabled ? "none" : undefined, margin: "0px !important", width: "100%", opacity: disabled ? 0.5 : 1 }}
    >
      <CardHeader
        sx={{ p: 0, pr: 2, backgroundColor: disabled || isSkip ? undefined : background.secondary }}
        expandIcon={<IconChevronDown color={text.secondary} width={20} height={20} />}
      >
        <Box className="title" bgcolor={disabled || isSkip ? button.disable : button.primary}>
          <Typography color={text.white} variant="sub16Bold">
            {title}
          </Typography>
        </Box>
        <Stack
          mt={1.8}
          ml={2}
          alignItems="center"
          justifyContent="center"
          width={20}
          height={20}
          borderRadius={20}
          bgcolor={isCompleted ? state.success_1 : divider.high}
        >
          <IconCheck color={background.white} />
        </Stack>
        <Typography component="p" mt={1.5} ml={2} variant="sub16Bold">
          {subTitle}
        </Typography>
      </CardHeader>
      <CardBody>
        <Box paddingX={3} paddingY={2}>
          {children}
        </Box>
        {buttons && (
          <Stack flexDirection="row" justifyContent="flex-end" px={3} paddingY={2} borderTop={`1px solid ${divider.high}`}>
            {buttons}
          </Stack>
        )}
      </CardBody>
    </MuiAccordionWrapper>
  );
};

const options = { shouldForwardProp: (propName: string) => !["disabledStep"].includes(propName) };

const MuiAccordionWrapper = styled(MuiAccordion, options)<{ disabledStep: boolean }>`
  ${({ disabledStep }) =>
    disabledStep &&
    css`
      .MuiButton-root {
        background: ${background.disable} !important;
        border: 2px solid ${background.disable} !important;
        color: ${text.tertiary} !important;
      }

      .MuiCheckbox-root {
        color: ${text.tertiary} !important;
      }
    `}

  &::before {
    display: none;
  }
  .MuiAccordionSummary-root {
    min-height: unset;
  }

  .MuiAccordionSummary-root.Mui-expanded {
    min-height: unset;
  }

  border: 1px solid ${divider.middle};
  border-radius: ${rounded.sm} !important;
  overflow: hidden;
  background-color: ${background.primary};

  .title {
    padding: 12px;
  }
`;

const CardHeader = styled(AccordionSummary)`
  padding: ${spacing.sm} ${spacing.md};
  display: flex;
  justify-content: space-between;
  align-items: center;

  .MuiAccordionSummary-content {
    margin: 0 !important;
  }
`;

const CardBody = styled(Box)`
  border-top: 1px solid ${divider.middle};
  background-color: ${background.white};
`;
